/**
 * useSwipeNavigation - スワイプジェスチャーによるナビゲーション
 */

import { useGesture } from '@use-gesture/react'
import { useRef, useCallback, useState, useEffect } from 'react'
import { useStore } from '../../../store'

interface UseSwipeNavigationOptions {
  enabled?: boolean
  threshold?: number // スワイプ認識の閾値（ピクセル）
  velocity?: number  // 速度閾値
}

export function useSwipeNavigation(
  containerRef: React.RefObject<HTMLElement>,
  options: UseSwipeNavigationOptions = {}
) {
  const {
    enabled = true,
    threshold = 50,
    velocity = 0.2
  } = options
  
  const nextImage = useStore(state => state.nextImage)
  const previousImage = useStore(state => state.previousImage)
  const selectedSetId = useStore(state => state.viewState.selectedSetId)
  const currentSet = useStore(state => {
    return selectedSetId ? state.appState.imageSets[selectedSetId] : undefined
  })
  const currentIndex = useStore(state => state.viewState.playback.currentIndex)
  
  // setItemsの長さだけを取得（配列全体ではなく）
  const itemsCount = useStore(state => {
    if (!selectedSetId) return 0
    const items = Object.values(state.appState.setItems)
      .filter(item => item.setId === selectedSetId)
    return items.length
  })
  
  const isSwipingRef = useRef(false)
  const startXRef = useRef(0)
  
  // SSR/hydration対応 - ウィンドウサイズを動的に取得
  const [windowWidth, setWindowWidth] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 800
  })
  
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  // スワイプ処理
  const handleSwipe = useCallback((direction: 'left' | 'right') => {
    if (!enabled || !currentSet) return
    
    if (direction === 'left') {
      // 左スワイプ = 次の画像
      if (currentIndex < itemsCount - 1 || currentSet.type === 'circular') {
        nextImage()
      }
    } else {
      // 右スワイプ = 前の画像
      if (currentIndex > 0 || currentSet.type === 'circular') {
        previousImage()
      }
    }
  }, [enabled, currentSet, currentIndex, itemsCount, nextImage, previousImage])
  
  // ジェスチャー設定
  useGesture(
    {
      onDragStart: ({ event, initial: [x] }) => {
        if (!enabled || !containerRef.current) return
        
        // タッチイベントまたはマウスイベントのみ処理
        if (event && (event.type === 'touchstart' || event.type === 'mousedown')) {
          isSwipingRef.current = true
          startXRef.current = x
        }
      },
      
      onDrag: ({ direction: [dx], cancel }) => {
        if (!enabled || !isSwipingRef.current) return
        
        // 垂直方向の動きが大きい場合はキャンセル
        if (Math.abs(dx) < 0.5) {
          cancel()
          isSwipingRef.current = false
          return
        }
        
        // lastの処理は削除し、onDragEndで統一処理する
      },
      
      onDragEnd: ({ movement: [mx], velocity: [vx] }) => {
        if (!enabled) {
          isSwipingRef.current = false
          return
        }
        
        // 速度または移動距離で判定してスワイプ処理
        if (Math.abs(vx) > velocity || Math.abs(mx) > threshold) {
          // 十分な速度または移動量がある場合、次/前の画像へ
          handleSwipe(mx < 0 ? 'left' : 'right')
        }
        // スナップ処理は削除 - VirtualizedImageListのscrollToIndexに任せる
        
        isSwipingRef.current = false
      }
    },
    {
      target: containerRef,
      enabled,
      drag: {
        filterTaps: true,
        axis: 'x',
        bounds: { left: -windowWidth, right: windowWidth },
        rubberband: 0.2
      }
    }
  )
  
  return {
    isSwipingRef
  }
}

/**
 * usePinchZoom - ピンチジェスチャーによるズーム
 */
interface UsePinchZoomOptions {
  enabled?: boolean
  minScale?: number
  maxScale?: number
}

export function usePinchZoom(
  elementRef: React.RefObject<HTMLElement>,
  options: UsePinchZoomOptions = {}
) {
  const {
    enabled = true,
    minScale = 1,
    maxScale = 3
  } = options
  
  const scaleRef = useRef(1)
  const offsetRef = useRef({ x: 0, y: 0 })
  
  // ズームリセット
  const resetZoom = useCallback(() => {
    if (!elementRef.current) return
    
    scaleRef.current = 1
    offsetRef.current = { x: 0, y: 0 }
    
    elementRef.current.style.transform = 'scale(1) translate(0, 0)'
    elementRef.current.style.transition = 'transform 0.3s ease-out'
    
    setTimeout(() => {
      if (elementRef.current) {
        elementRef.current.style.transition = ''
      }
    }, 300)
  }, [elementRef])
  
  // ピンチジェスチャー設定
  useGesture(
    {
      onPinch: ({ offset: [scale] }) => {
        if (!enabled || !elementRef.current) return
        
        // スケールを制限
        const boundedScale = Math.min(Math.max(scale, minScale), maxScale)
        scaleRef.current = boundedScale
        
        // 変換を適用
        elementRef.current.style.transform = `scale(${boundedScale})`
      },
      
      onPinchEnd: () => {
        // スケールが1に近い場合はリセット
        if (Math.abs(scaleRef.current - 1) < 0.1) {
          resetZoom()
        }
      },
      
      // ダブルタップでズームリセット
      onDoubleClick: () => {
        if (!enabled) return
        resetZoom()
      },
      
      // パン操作（ズーム時のみ）
      onDrag: ({ movement: [mx, my] }) => {
        if (!enabled || !elementRef.current || scaleRef.current <= 1) return
        
        offsetRef.current = { x: mx, y: my }
        elementRef.current.style.transform = 
          `scale(${scaleRef.current}) translate(${mx}px, ${my}px)`
      }
    },
    {
      target: elementRef,
      pinch: {
        scaleBounds: { min: minScale, max: maxScale },
        rubberband: true
      },
      drag: {
        enabled: scaleRef.current > 1,
        bounds: {
          left: -100 * (scaleRef.current - 1),
          right: 100 * (scaleRef.current - 1),
          top: -100 * (scaleRef.current - 1),
          bottom: 100 * (scaleRef.current - 1)
        }
      }
    }
  )
  
  return {
    scale: scaleRef.current,
    resetZoom
  }
}

/**
 * useKeyboardNavigation - キーボードショートカット
 */
export function useKeyboardNavigation(enabled = true) {
  const nextImage = useStore(state => state.nextImage)
  const previousImage = useStore(state => state.previousImage)
  const navigateTo = useStore(state => state.navigateTo)
  const setPlaybackState = useStore(state => state.setPlaybackState)
  const isPlaying = useStore(state => state.viewState.isPlaying)
  
  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    if (!enabled) return
    
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault()
        previousImage()
        break
      case 'ArrowRight':
        e.preventDefault()
        nextImage()
        break
      case ' ':
        e.preventDefault()
        setPlaybackState(!isPlaying)
        break
      case 'Escape':
        e.preventDefault()
        navigateTo('decks')
        break
      case 'f':
        if (!e.metaKey && !e.ctrlKey) {
          e.preventDefault()
          // フルスクリーン切り替え
          if (!document.fullscreenElement) {
            void document.documentElement.requestFullscreen()
          } else {
            void document.exitFullscreen()
          }
        }
        break
    }
  }, [enabled, nextImage, previousImage, navigateTo, setPlaybackState, isPlaying])
  
  // イベントリスナー登録
  useGesture(
    {},
    {
      target: window,
      eventOptions: {
        passive: false
      }
    }
  )
  
  return {
    handleKeyPress
  }
}